import { useState, useEffect } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { useAuth } from '../context/AuthContext';
import { USER_ROLES } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2,
  Building2,
  Plus,
  ShieldAlert,
  Users,
  Pencil,
  Power,
  PowerOff,
  KeyRound,
  UserPen
} from 'lucide-react';

const SuperAdmin = () => {
  const { user } = useAuth();
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    _id: '',
    name: '',
    rut: '',
  });
  
  // User Management State
  const [selectedMuni, setSelectedMuni] = useState(null);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [muniUsers, setMuniUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isNewUserSectionOpen, setIsNewUserSectionOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [editUserFormData, setEditUserFormData] = useState({
    user_id: '',
    name: '',
    email: '',
    role: 'social_worker',
  });
  const [passwordFormData, setPasswordFormData] = useState({
    user_id: '',
    user_name: '',
    password: '',
    confirm_password: '',
  });
  
  const [newUserFormData, setNewUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'social_worker',
  });
  
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });

  const fetchData = async () => {
    try {
      const response = await api.get('/municipalities/');
      setMunicipalities(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tiene permisos de Super Administrador');
      } else {
        toast.error('Error al cargar municipalidades');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.rut || !formData.admin_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Complete todos los campos');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // 1. Create Municipality
      const muniRes = await api.post('/municipalities/', {
        name: formData.name,
        rut: formData.rut,
        logo_url: ""
      });
      const newMuniId = muniRes.data._id;

      // 2. Create the first Admin for that Municipality
      await api.post(`/municipalities/${newMuniId}/admin`, {
        name: formData.admin_name,
        email: formData.admin_email,
        password: formData.admin_password,
        role: "admin"
      });

      toast.success('Municipalidad y Administrador creados exitosamente');
      setIsModalOpen(false);
      setFormData({ name: '', rut: '', admin_name: '', admin_email: '', admin_password: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error en el proceso de creación'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (muni) => {
    setEditFormData({
      _id: muni._id,
      name: muni.config?.name || '',
      rut: muni.config?.rut || '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.name || !editFormData.rut) {
      toast.error('Complete todos los campos');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await api.put(`/municipalities/${editFormData._id}`, {
        name: editFormData.name,
        rut: editFormData.rut
      });
      toast.success('Municipalidad actualizada exitosamente');
      setIsEditModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar municipalidad'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (muni) => {
    try {
      if (muni.is_active) {
        // Deactivate (Logical Delete)
        await api.delete(`/municipalities/${muni._id}`);
        toast.success('Municipalidad desactivada');
      } else {
        // Activate
        await api.put(`/municipalities/${muni._id}`, { is_active: true });
        toast.success('Municipalidad reactivada');
      }
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar el estado'));
    }
  };

  const fetchUsersForMuni = async (muniId) => {
    setLoadingUsers(true);
    try {
      const resp = await api.get(`/municipalities/${muniId}/users`);
      setMuniUsers(resp.data);
    } catch (error) {
      toast.error('Error al cargar usuarios de la municipalidad');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpenUsersModal = (muni) => {
    setSelectedMuni(muni);
    setIsUsersModalOpen(true);
    setIsNewUserSectionOpen(false);
    fetchUsersForMuni(muni._id);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserFormData.name || !newUserFormData.email || !newUserFormData.password) {
      toast.error('Complete todos los campos del nuevo usuario');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/municipalities/${selectedMuni._id}/users`, newUserFormData);
      toast.success('Usuario creado exitosamente');
      setNewUserFormData({ name: '', email: '', password: '', role: 'social_worker' });
      setIsNewUserSectionOpen(false);
      fetchUsersForMuni(selectedMuni._id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear usuario'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/municipalities/${selectedMuni._id}/users/${userId}/role`, null, { params: { role: newRole } });
      toast.success('Rol de usuario actualizado');
      fetchUsersForMuni(selectedMuni._id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar rol'));
    }
  };

  const handleEditUserClick = (selectedUser) => {
    setEditUserFormData({
      user_id: selectedUser.user_id,
      name: selectedUser.name || '',
      email: selectedUser.email || '',
      role: selectedUser.role || 'social_worker',
    });
    setIsEditUserModalOpen(true);
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    if (!editUserFormData.name || !editUserFormData.email) {
      toast.error('Complete nombre y correo del usuario');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.put(`/municipalities/${selectedMuni._id}/users/${editUserFormData.user_id}`, {
        name: editUserFormData.name,
        email: editUserFormData.email,
        role: editUserFormData.role,
      });
      toast.success('Usuario actualizado');
      setIsEditUserModalOpen(false);
      fetchUsersForMuni(selectedMuni._id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al editar usuario'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenPasswordModal = (selectedUser) => {
    setPasswordFormData({
      user_id: selectedUser.user_id,
      user_name: selectedUser.name || selectedUser.email,
      password: '',
      confirm_password: '',
    });
    setIsPasswordModalOpen(true);
  };

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    if (!passwordFormData.password) {
      toast.error('Ingrese una clave');
      return;
    }
    if (passwordFormData.password.length < 6) {
      toast.error('La clave debe tener al menos 6 caracteres');
      return;
    }
    if (passwordFormData.password !== passwordFormData.confirm_password) {
      toast.error('Las claves no coinciden');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.put(`/municipalities/${selectedMuni._id}/users/${passwordFormData.user_id}/password`, {
        password: passwordFormData.password
      });
      toast.success('Clave actualizada exitosamente');
      setIsPasswordModalOpen(false);
      setPasswordFormData({ user_id: '', user_name: '', password: '', confirm_password: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear clave'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <ShieldAlert className="w-12 h-12 mb-4 text-rose-500" />
        <p className="text-lg font-medium text-slate-900">Acceso Denegado</p>
        <p className="text-sm">Esta sección es exclusiva para Super Administradores del sistema.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="superadmin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Panel de Súper Administrador
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de Inquilinos y Alta de nuevas corporaciones
          </p>
        </div>
        
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/20" data-testid="new-muni-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Municipalidad
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Alta de Nueva Municipalidad SaaS</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-6 pt-4">
              
              {/* Municipality Data */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 border-b pb-2">1. Datos de la Entidad</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre de la Municipalidad</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Ilustre Municipalidad de Santiago"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rut">RUT Entidad</Label>
                    <Input
                      id="rut"
                      value={formData.rut}
                      onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                      placeholder="Ej: 69.123.456-7"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Admin User Data */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 border-b pb-2">2. Administrador Principal</h3>
                <p className="text-xs text-slate-500 -mt-2">Esta será la cuenta que tendrá control total de este nuevo municipio para invitar a su propio personal.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_name">Nombre del Admin</Label>
                    <Input
                      id="admin_name"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin_email">Correo de Acceso (Email)</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="admin_password">Contraseña (Temporal)</Label>
                    <Input
                      id="admin_password"
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando Entorno...</>
                  ) : (
                    'Crear y Asignar Entorno'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 text-purple-700 rounded-lg">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Inquilinos Activos</p>
              <p className="text-2xl font-bold text-slate-900">{municipalities.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Inquilinos Registrados</CardTitle>
          <CardDescription>Lista de bases de datos aisladas disponibles en el sistema.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : municipalities.length === 0 ? (
             <div className="text-center py-12">
                <p className="text-slate-500">Aún no hay municipalidades en el sistema.</p>
             </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID de Base de Datos</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha de Creación</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {municipalities.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell className="font-mono text-xs text-slate-500">{item._id}</TableCell>
                      <TableCell className="font-medium text-slate-900">{item.config?.name}</TableCell>
                      <TableCell>{item.config?.rut}</TableCell>
                      <TableCell>
                        {item.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-800">Suspendido</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                            onClick={() => handleOpenUsersModal(item)}
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                            onClick={() => handleEditClick(item)}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className={item.is_active ? "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200" : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"}
                            onClick={() => handleToggleStatus(item)}
                            title={item.is_active ? "Desactivar" : "Activar"}
                          >
                            {item.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Municipality Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Municipalidad</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Nombre de la Municipalidad</Label>
              <Input
                id="edit_name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_rut">RUT Entidad</Label>
              <Input
                id="edit_rut"
                value={editFormData.rut}
                onChange={(e) => setEditFormData({ ...editFormData, rut: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Guardar Cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Users Modal */}
      <Dialog open={isUsersModalOpen} onOpenChange={setIsUsersModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Usuarios de la Municipalidad: {selectedMuni?.config?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-between items-center mt-4">
            <h3 className="text-lg font-medium text-slate-900">Personal Registrado</h3>
            <Button 
              onClick={() => setIsNewUserSectionOpen(!isNewUserSectionOpen)}
              variant="outline"
              className={isNewUserSectionOpen ? "bg-slate-100" : ""}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isNewUserSectionOpen ? 'Cancelar' : 'Nuevo Usuario'}
            </Button>
          </div>

          {isNewUserSectionOpen && (
            <Card className="mt-4 border-dashed border-2 bg-slate-50/50">
              <CardContent className="pt-6">
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-userName">Nombre</Label>
                      <Input
                        id="new-userName"
                        value={newUserFormData.name}
                        onChange={(e) => setNewUserFormData({ ...newUserFormData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-userEmail">Correo Electrónico</Label>
                      <Input
                        id="new-userEmail"
                        type="email"
                        value={newUserFormData.email}
                        onChange={(e) => setNewUserFormData({ ...newUserFormData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-userPassword">Contraseña</Label>
                      <Input
                        id="new-userPassword"
                        type="password"
                        value={newUserFormData.password}
                        onChange={(e) => setNewUserFormData({ ...newUserFormData, password: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rol del Sistema</Label>
                      <Select 
                        value={newUserFormData.role} 
                        onValueChange={(val) => setNewUserFormData({ ...newUserFormData, role: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione un rol" />
                        </SelectTrigger>
                        <SelectContent>
                          {USER_ROLES.map(({ value, label }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Guardar Usuario
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="mt-6">
            {loadingUsers ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : muniUsers.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-slate-200">
                No hay usuarios registrados en esta municipalidad.
              </div>
            ) : (
              <div className="rounded-md border text-sm">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol Actual</TableHead>
                      <TableHead>Fecha Creación</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {muniUsers.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={user.picture} />
                              <AvatarFallback className="bg-purple-100 text-purple-700 text-xs">
                                {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-slate-900">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {USER_ROLES.find(r => r.value === user.role)?.label || user.role.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                              onClick={() => handleEditUserClick(user)}
                            >
                              <UserPen className="w-3.5 h-3.5 mr-1" />
                              Editar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
                              onClick={() => handleOpenPasswordModal(user)}
                            >
                              <KeyRound className="w-3.5 h-3.5 mr-1" />
                              Crear clave
                            </Button>
                            <Select
                              value={user.role}
                              onValueChange={(newRole) => handleRoleChange(user.user_id, newRole)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Cambiar Rol a:
                                </div>
                                {USER_ROLES.map(({ value, label }) => (
                                  <SelectItem key={value} value={value} className="text-xs">
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditUserModalOpen} onOpenChange={setIsEditUserModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUserSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="edit-user-name">Nombre</Label>
              <Input
                id="edit-user-name"
                value={editUserFormData.name}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-email">Correo</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={editUserFormData.email}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={editUserFormData.role}
                onValueChange={(value) => setEditUserFormData({ ...editUserFormData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione un rol" />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditUserModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear clave de usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePassword} className="space-y-4 pt-2">
            <div className="text-sm text-slate-600">
              Usuario: <span className="font-medium text-slate-900">{passwordFormData.user_name || '-'}</span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva clave</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordFormData.password}
                onChange={(e) => setPasswordFormData({ ...passwordFormData, password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar clave</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordFormData.confirm_password}
                onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPasswordModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar clave
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdmin;
